import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { UserAPI } from '@certi/shared/api/user/UserAPI'
import type { UserProfileResponse } from '@certi/shared/types'
import { useAuth } from '@/hooks/useAuth'
import SecuritySection from '@/components/profile/SecuritySection'

interface SettingsSectionProps {
  profile: UserProfileResponse
  onUpdate: (updatedProfile: UserProfileResponse) => void
}

export default function SettingsSection({ profile, onUpdate }: SettingsSectionProps) {
  const { user, session, signOut, syncUserProfile } = useAuth()
  const navigate = useNavigate()
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [formData, setFormData] = useState({
    firstName: profile.firstName || '',
    lastName: profile.lastName || '',
    phone: profile.phone || '',
    dateOfBirth: profile.dateOfBirth || '',
    gender: profile.gender || ''
  })

  useEffect(() => {
    setFormData({
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      phone: profile.phone || '',
      dateOfBirth: profile.dateOfBirth || '',
      gender: profile.gender || ''
    })
  }, [profile])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSave = async () => {
    if (!user?.id || !session?.accessToken) return

    try {
      setLoading(true)
      setError(null)

      // Update profile
      const updatedProfile = await UserAPI.updateProfile(
        user.id,
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          dateOfBirth: formData.dateOfBirth || undefined,
          gender: formData.gender || undefined,
        },
        session.accessToken
      )

      syncUserProfile(updatedProfile)
      onUpdate(updatedProfile)
      setIsEditing(false)
    } catch (err) {
      console.error('Failed to update profile:', err)
      setError(err instanceof Error ? err.message : 'Failed to update profile')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setFormData({
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      phone: profile.phone || '',
      dateOfBirth: profile.dateOfBirth || '',
      gender: profile.gender || ''
    })
    setError(null)
    setIsEditing(false)
  }

  const handleDeleteAccount = () => {
    setShowDeleteModal(true)
  }

  const confirmDeleteAccount = async () => {
    if (!user?.id || !session?.accessToken) return

    try {
      setLoading(true)
      setError(null)

      // Call API to delete account
      await UserAPI.deleteAccount(user.id, session.accessToken)

      // Close modal
      setShowDeleteModal(false)

      await signOut()
      navigate('/', { replace: true })
    } catch (err) {
      console.error('Failed to delete account:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete account')
      setShowDeleteModal(false)
    } finally {
      setLoading(false)
    }
  }

  const handleManageAddresses = () => {
    navigate('/profile/addresses')
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Account Details */}
      <div className="border-b border-gray-200 pb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Account details</h3>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="text-brand-primary hover:text-brand-hover font-medium text-sm"
            >
              Change
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-4 py-2 text-sm text-white bg-brand-primary rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>

        {!isEditing ? (
          <div className="space-y-1">
            <div className="flex gap-32 py-1">
              <span className="text-gray-600 w-56">First name</span>
              <span className="text-gray-900 font-medium">{formData.firstName || '-'}</span>
            </div>
            <div className="flex gap-32 py-1">
              <span className="text-gray-600 w-56">Last name</span>
              <span className="text-gray-900 font-medium">{formData.lastName || '-'}</span>
            </div>
            <div className="flex gap-32 py-1">
              <span className="text-gray-600 w-56">Email address</span>
              <span className="text-gray-900 font-medium">{profile.email || '-'}</span>
            </div>
            <div className="flex gap-32 py-1">
              <span className="text-gray-600 w-56">Phone number</span>
              <span className="text-gray-900 font-medium">{formData.phone || '-'}</span>
            </div>
            <div className="flex gap-32 py-1">
              <span className="text-gray-600 w-56">Date of Birth</span>
              <span className="text-gray-900 font-medium">{formData.dateOfBirth || '-'}</span>
            </div>
            <div className="flex gap-32 py-1">
              <span className="text-gray-600 w-56">Gender</span>
              <span className="text-gray-900 font-medium">{formData.gender || '-'}</span>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First name</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
              <input
                type="email"
                value={profile.email}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Change email and password in the Security section below.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone number</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Address */}
      <div className="border-b border-gray-200 pb-6">
        <div className="flex gap-32 py-1">
          <span className="text-gray-600 w-56">Address</span>
          <button
            onClick={handleManageAddresses}
            className="text-brand-primary hover:text-brand-hover font-medium"
          >
            Manage addresses
          </button>
        </div>
      </div>

      <div className="border-b border-gray-200 pb-6">
        <SecuritySection
          profile={profile}
          onUpdate={onUpdate}
        />
      </div>

      {/* Delete Account */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Account</h3>
        <p className="text-sm text-gray-600 mb-4">
          Once you delete your account, there is no going back. Please be certain.
        </p>
        <button
          onClick={handleDeleteAccount}
          className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700"
        >
          Delete My Account
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            {/* Warning Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">
              Delete Account?
            </h3>

            {/* Warning Message */}
            <p className="text-sm text-gray-600 text-center mb-6">
              This action <span className="font-semibold text-red-600">cannot be undone</span>.
              Your account will be anonymized and deactivated. Historical bids, listings, and transaction
              records may be retained without your personal profile information.
            </p>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={loading}
                className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteAccount}
                disabled={loading}
                className="flex-1 px-4 py-3 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
